import { useCallback, useEffect, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  type MutationPageState,
  type QueryPageState,
} from '#packages/data-collection/types/page-state';
import {
  AuditEcmsFormSchema,
  type AuditEcmsForm,
} from '#packages/data-collection/models/audit-ecms-form';

import { fromFormData, toFormData } from './mappers';
import {
  EditAuditFormEcmsDataSchema,
  type EditAuditFormEcmsData,
} from './types';

export type EcmsFormPageViewModel = ReturnType<
  typeof useAuditFormEcmsPageViewModel
>;

type AuditFormEcmsPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  queryClient: QueryClient;
};

export const useAuditFormEcmsPageViewModel = ({
  auditFormService,
  queryClient,
  logger,
}: AuditFormEcmsPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId } = useParams<{ auditId: AuditId }>();

  const auditFormQuery = useLoadAuditFormByType<AuditEcmsForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'ecms',
    queryClient,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditEcmsForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'ecms',
  });

  const form = useForm({
    resolver: zodResolver(
      EditAuditFormEcmsDataSchema,
    ) as Resolver<EditAuditFormEcmsData>,
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues: toFormData(undefined),
  });

  const onSave = useCallback(
    (formData: EditAuditFormEcmsData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug('Cannot update ECMs, page is not in success state');
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating ECMs form data');

      const previousDataEncoded = auditFormQuery.data
        ? AuditEcmsFormSchema.encode(auditFormQuery.data)
        : { type: 'ecms' as const };

      const auditEcmsForm = AuditEcmsFormSchema.decode({
        ...previousDataEncoded,
        ...fromFormData(formData),
      });

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditEcmsForm },
        {
          onSuccess: (savedForm: AuditEcmsForm) => {
            logger?.debug(
              { auditId, savedForm },
              'ECMs form updated successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('ECMs form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update ECMs form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      auditId,
      auditFormQuery.data,
      AuditEcmsFormSchema.encode,
      AuditEcmsFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) {
      return { state: 'idle' };
    }

    if (saveAuditFormMutation.isPending) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<AuditEcmsForm | null>
  >(() => {
    if (auditFormQuery.isSuccess) {
      return { state: 'success', data: auditFormQuery.data };
    }

    if (auditFormQuery.isLoading || auditFormQuery.isPending) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return { state: 'error', error: auditFormQuery.error };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
  ]);

  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditEcmsForm: auditFormQuery.data },
      'Audit ECMs form default values',
    );

    form.reset(toFormData(loadAuditFormState.data ?? undefined));
  }, [auditFormQuery.data, auditFormQuery.isSuccess]);

  return {
    auditFormQuery,
    loadAuditFormState,
    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,
    auditId,
  };
};
